export interface IntakeHelpDoc {
  title: string;
  url: string;
  relevance?: string;
}

export interface IntakeKnowledgebaseMatch {
  matched: boolean;
  entry_id?: string;
  adjusted_estimate_minutes?: number;
  self_service_possible?: boolean;
}

export type IntakeScopingStatus = "ok" | "unavailable";

export interface IntakeAIScopingApiResponse {
  category: string;
  scope_summary: string[];
  assumptions: string[];
  missing_info: string[];
  estimate_hours_number: number;
  estimate_bucket?: "small" | "medium" | "large";
  recommended_next_steps: string;
  confidence_score?: number;
  help_docs?: IntakeHelpDoc[];
  knowledgebase_match?: IntakeKnowledgebaseMatch;
  requiresPayment?: boolean;
  coverageReason?: string;
  matchedClientId?: string;
  scopingStatus?: IntakeScopingStatus;
}

export interface IntakeAIScopingInternalResponse {
  category: string;
  scopeSummary: string[];
  assumptions: string[];
  missingInfo: string[];
  estimateHours: number;
  estimateBucket?: "small" | "medium" | "large";
  recommendedNextSteps: string;
  confidenceScore?: number;
  helpDocs?: IntakeHelpDoc[];
  knowledgebaseMatch?: IntakeKnowledgebaseMatch;
  requiresPayment?: boolean;
  coverageReason?: string;
  matchedClientId?: string;
  scopingStatus?: IntakeScopingStatus;
  rawPrompt?: string;
  rawResponse?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
}

function toOptionalNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function toOptionalString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function normalizeBucket(
  value: unknown,
  fallback?: "small" | "medium" | "large",
): "small" | "medium" | "large" | undefined {
  return value === "small" || value === "medium" || value === "large"
    ? value
    : fallback;
}

function normalizeScopingStatus(value: unknown): IntakeScopingStatus | undefined {
  if (value === "ok" || value === "unavailable") return value;
  return undefined;
}

export function isScopingUnavailable(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    normalizeScopingStatus(value.scopingStatus) === "unavailable" ||
    normalizeScopingStatus(value.scoping_status) === "unavailable"
  );
}

export function createUnavailableAIScoping(
  category: string,
): IntakeAIScopingInternalResponse {
  return {
    scopingStatus: "unavailable",
    category,
    scopeSummary: [],
    assumptions: [],
    missingInfo: [],
    estimateHours: 0,
    recommendedNextSteps: "",
    confidenceScore: 0,
  };
}

export function createUnavailableAIScopingApiResponse(
  category: string,
): IntakeAIScopingApiResponse {
  return {
    scopingStatus: "unavailable",
    category,
    scope_summary: [],
    assumptions: [],
    missing_info: [],
    estimate_hours_number: 0,
    recommended_next_steps: "",
    confidence_score: 0,
  };
}

export function workRequestAiFieldsFromScoping(
  aiScoping?: IntakeAIScopingInternalResponse | null,
) {
  if (!aiScoping || isScopingUnavailable(aiScoping)) {
    return {
      aiScopeSummary: null,
      aiAssumptions: null,
      aiMissingInfo: null,
      aiEstimateHours: null,
      aiEstimateBucket: null,
      aiConfidenceScore: null,
      aiRecommendedNextSteps: null,
      aiRawPrompt: null,
      aiRawResponse: null,
    };
  }

  return {
    aiScopeSummary: aiScoping.scopeSummary.join("\n") || null,
    aiAssumptions: aiScoping.assumptions,
    aiMissingInfo: aiScoping.missingInfo,
    aiEstimateHours: aiScoping.estimateHours,
    aiEstimateBucket: aiScoping.estimateBucket ?? null,
    aiConfidenceScore: aiScoping.confidenceScore ?? null,
    aiRecommendedNextSteps: aiScoping.recommendedNextSteps || null,
    aiRawPrompt: aiScoping.rawPrompt ?? null,
    aiRawResponse: aiScoping.rawResponse ?? null,
  };
}

function normalizeHelpDocs(value: unknown): IntakeHelpDoc[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const docs: IntakeHelpDoc[] = [];

  for (const item of value) {
    if (!isRecord(item)) continue;

    const title = toOptionalString(item.title);
    const url = toOptionalString(item.url);
    const relevance = toOptionalString(item.relevance);

    if (!title || !url) continue;

    docs.push({
      title,
      url,
      ...(relevance ? { relevance } : {}),
    });
  }

  return docs.length > 0 ? docs : undefined;
}

function normalizeKnowledgebaseMatch(
  value: unknown,
): IntakeKnowledgebaseMatch | undefined {
  if (!isRecord(value)) return undefined;

  return {
    matched: Boolean(value.matched),
    entry_id: toOptionalString(value.entry_id),
    adjusted_estimate_minutes: toOptionalNumber(value.adjusted_estimate_minutes),
    self_service_possible:
      typeof value.self_service_possible === "boolean"
        ? value.self_service_possible
        : undefined,
  };
}

export function normalizeAIScopingResponse(
  value: unknown,
): IntakeAIScopingApiResponse {
  const record = isRecord(value) ? value : {};
  const scopingStatus = normalizeScopingStatus(
    record.scopingStatus ?? record.scoping_status,
  );
  const unavailable = scopingStatus === "unavailable";

  const scopeSummary = toStringArray(
    record.scope_summary ?? record.scopeSummary,
  );
  const assumptions = toStringArray(record.assumptions);
  const missingInfo = toStringArray(record.missing_info ?? record.missingInfo);
  const estimateHours =
    toOptionalNumber(
      record.estimate_hours_number ?? record.estimateHours,
    ) ?? (unavailable ? 0 : 1);

  return {
    category: toOptionalString(record.category) ?? "",
    scope_summary: scopeSummary,
    assumptions,
    missing_info: missingInfo,
    estimate_hours_number: estimateHours,
    estimate_bucket: normalizeBucket(
      record.estimate_bucket ?? record.estimateBucket,
      unavailable ? undefined : "medium",
    ),
    recommended_next_steps:
      toOptionalString(
        record.recommended_next_steps ?? record.recommendedNextSteps,
      ) ?? "",
    confidence_score: toOptionalNumber(
      record.confidence_score ?? record.confidenceScore,
    ),
    help_docs: normalizeHelpDocs(record.help_docs ?? record.helpDocs),
    knowledgebase_match: normalizeKnowledgebaseMatch(
      record.knowledgebase_match ?? record.knowledgebaseMatch,
    ),
    requiresPayment:
      typeof record.requiresPayment === "boolean"
        ? record.requiresPayment
        : undefined,
    coverageReason: toOptionalString(record.coverageReason),
    matchedClientId: toOptionalString(record.matchedClientId),
    scopingStatus,
  };
}

export function toAIScopingInternal(
  value: unknown,
): IntakeAIScopingInternalResponse {
  const normalized = normalizeAIScopingResponse(value);

  return {
    category: normalized.category,
    scopeSummary: normalized.scope_summary,
    assumptions: normalized.assumptions,
    missingInfo: normalized.missing_info,
    estimateHours: normalized.estimate_hours_number,
    estimateBucket: normalized.estimate_bucket,
    recommendedNextSteps: normalized.recommended_next_steps,
    confidenceScore: normalized.confidence_score,
    helpDocs: normalized.help_docs,
    knowledgebaseMatch: normalized.knowledgebase_match,
    requiresPayment: normalized.requiresPayment,
    coverageReason: normalized.coverageReason,
    matchedClientId: normalized.matchedClientId,
    scopingStatus: normalized.scopingStatus,
  };
}
